import React, { useState, useEffect } from 'react';
import { Cloud, Droplets, Thermometer, Activity, TrendingUp, CheckCircle, XCircle } from 'lucide-react';

interface WeatherLocation {
  latitude: number;
  longitude: number;
  display_name: string;
  timezone: string;
}

interface CurrentWeather {
  temperature_celsius: number;
  feels_like_celsius: number;
  humidity_percent: number;
  wind_speed_ms: number;
  wind_direction_deg: number;
  pressure_hpa: number;
  precipitation_mm: number;
  condition: string;
  timestamp: string;
}

interface HourlyForecast {
  timestamp: string;
  temperature_celsius: number;
  precipitation_probability: number;
  precipitation_mm: number;
  wind_speed_ms: number;
  condition: string;
}

interface DailyForecast {
  date: string;
  temp_high_celsius: number;
  temp_low_celsius: number;
  precipitation_probability: number;
  precipitation_mm: number;
  condition: string;
}

interface Forecast {
  location: WeatherLocation;
  current: CurrentWeather;
  hourly: HourlyForecast[];
  daily: DailyForecast[];
  generated_at: string;
  source: string;
}

interface ComparisonMetrics {
  status: string;
  reason?: string;  // Optional reason when status is 'incomplete'
  current?: {
    temperature_difference_celsius: number;
    feels_like_difference_celsius?: number;
    humidity_difference_percent: number;
    wind_speed_difference_ms: number;
    pressure_difference_hpa?: number;
    condition_match: boolean;
  };
  hourly_comparison?: Array<{
    hour: number;
    temp_diff_celsius: number;
    precip_prob_diff: number;
    wind_diff_ms: number;
    condition_match: boolean;
  }>;
  daily_comparison?: Array<{
    day: number;
    date: string;
    temp_high_diff_celsius: number;
    temp_low_diff_celsius: number;
    precip_prob_diff?: number;
    condition_match: boolean;
  }>;
  summary?: {
    average_temperature_difference_celsius: number;
    hourly_condition_agreement_percent: number;
    daily_condition_agreement_percent: number;
    interpretation: string;
  };
}

interface ComparisonData {
  location: WeatherLocation;
  earth2studio_forecast: Forecast | null;
  openweathermap_forecast: Forecast | null;
  comparison: ComparisonMetrics;
  timestamp: string;
  sources_available: {
    earth2studio: boolean;
    openweathermap: boolean;
  };
}

interface WeatherComparisonProps {
  latitude: number;
  longitude: number;
  locationName: string;
  timezone?: string;
}

const WeatherComparison: React.FC<WeatherComparisonProps> = ({
  latitude,
  longitude,
  locationName,
  timezone = 'UTC',
}) => {
  const [comparison, setComparison] = useState<ComparisonData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchComparison();
  }, [latitude, longitude, locationName, timezone]);

  const fetchComparison = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
      const params = new URLSearchParams({
        latitude: latitude.toString(),
        longitude: longitude.toString(),
        location_name: locationName,
        timezone,
      });

      const response = await fetch(`${apiUrl}/api/v1/weather/compare?${params}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch comparison: ${response.statusText}`);
      }

      const data: ComparisonData = await response.json();
      setComparison(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      console.error('Error fetching comparison:', err);
    } finally {
      setLoading(false);
    }
  };

  const getConditionIcon = (condition: string) => {
    const iconClass = 'w-6 h-6';
    switch (condition.toLowerCase()) {
      case 'clear':
        return <div className={`${iconClass} text-yellow-400`}>☀️</div>;
      case 'cloudy':
        return <Cloud className={iconClass} />;
      case 'rain':
        return <Droplets className={iconClass} />;
      case 'storm':
        return <Activity className={iconClass} />;
      default:
        return <Cloud className={iconClass} />;
    }
  };

  const getAgreementColor = (percent: number) => {
    if (percent >= 80) return 'text-green-600';
    if (percent >= 70) return 'text-blue-600';
    if (percent >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getInterpretationBadge = (interpretation: string) => {
    if (interpretation.includes('Excellent')) {
      return 'bg-green-100 text-green-800 border-green-300';
    }
    if (interpretation.includes('Good')) {
      return 'bg-blue-100 text-blue-800 border-blue-300';
    }
    if (interpretation.includes('Moderate')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    }
    return 'bg-red-100 text-red-800 border-red-300';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className="ml-4 text-gray-600">Loading weather comparison...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error: {error}</p>
        <button
          onClick={fetchComparison}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!comparison) {
    return <div className="text-gray-600">No comparison data available</div>;
  }

  const { earth2studio_forecast, openweathermap_forecast, comparison: metrics } = comparison;

  // Check if comparison is incomplete
  const isIncomplete = metrics.status === 'incomplete';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-lg p-6 shadow-lg">
        <h2 className="text-2xl font-bold mb-2">Weather Forecast Comparison</h2>
        <p className="text-blue-100">
          AI Predictions vs Real-World Data for {comparison.location.display_name}
        </p>
      </div>

      {/* Show warning if comparison is incomplete */}
      {isIncomplete && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Comparison Unavailable:</strong> {metrics.reason || 'One or both weather sources are not available'}
              </p>
              <p className="text-xs text-yellow-600 mt-1">
                Note: Earth-2 Studio requires GPU server deployment. Currently showing available data only.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Metrics - Only show if comparison is complete */}
      {!isIncomplete && metrics.summary && (
        <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
          <h3 className="text-xl font-semibold mb-4 flex items-center">
            <TrendingUp className="w-6 h-6 mr-2 text-blue-600" />
            Comparison Summary
          </h3>

          <div className={`mb-4 px-4 py-3 rounded-lg border-2 ${getInterpretationBadge(metrics.summary.interpretation)}`}>
            <p className="font-medium">{metrics.summary.interpretation}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Average Temperature Difference */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-2">
                <Thermometer className="w-5 h-5 text-orange-500 mr-2" />
                <span className="text-sm text-gray-600 font-medium">Avg Temp Difference</span>
              </div>
              <p className="text-2xl font-bold text-gray-800">
                {metrics.summary.average_temperature_difference_celsius.toFixed(1)}°C
              </p>
            </div>

            {/* Hourly Agreement */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-2">
                <Activity className="w-5 h-5 text-blue-500 mr-2" />
                <span className="text-sm text-gray-600 font-medium">Hourly Agreement</span>
              </div>
              <p className={`text-2xl font-bold ${getAgreementColor(metrics.summary.hourly_condition_agreement_percent)}`}>
                {metrics.summary.hourly_condition_agreement_percent.toFixed(1)}%
              </p>
            </div>

            {/* Daily Agreement */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-2">
                <Cloud className="w-5 h-5 text-indigo-500 mr-2" />
                <span className="text-sm text-gray-600 font-medium">Daily Agreement</span>
              </div>
              <p className={`text-2xl font-bold ${getAgreementColor(metrics.summary.daily_condition_agreement_percent)}`}>
                {metrics.summary.daily_condition_agreement_percent.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Conditions Comparison */}
      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4">Current Conditions</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Earth-2 Studio */}
          {earth2studio_forecast && (
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-blue-900">AI Forecast (Earth-2 Studio)</h4>
                {getConditionIcon(earth2studio_forecast.current.condition)}
              </div>
              <div className="space-y-2 text-sm">
                <p className="flex justify-between">
                  <span className="text-gray-600">Temperature:</span>
                  <span className="font-medium">{earth2studio_forecast.current.temperature_celsius.toFixed(1)}°C</span>
                </p>
                <p className="flex justify-between">
                  <span className="text-gray-600">Feels Like:</span>
                  <span className="font-medium">{earth2studio_forecast.current.feels_like_celsius.toFixed(1)}°C</span>
                </p>
                <p className="flex justify-between">
                  <span className="text-gray-600">Humidity:</span>
                  <span className="font-medium">{earth2studio_forecast.current.humidity_percent}%</span>
                </p>
                <p className="flex justify-between">
                  <span className="text-gray-600">Wind Speed:</span>
                  <span className="font-medium">{earth2studio_forecast.current.wind_speed_ms.toFixed(1)} m/s</span>
                </p>
                <p className="flex justify-between">
                  <span className="text-gray-600">Pressure:</span>
                  <span className="font-medium">{earth2studio_forecast.current.pressure_hpa.toFixed(0)} hPa</span>
                </p>
              </div>
            </div>
          )}

          {/* OpenWeatherMap */}
          {openweathermap_forecast && (
            <div className="border border-green-200 rounded-lg p-4 bg-green-50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-green-900">Real Data (OpenWeatherMap)</h4>
                {getConditionIcon(openweathermap_forecast.current.condition)}
              </div>
              <div className="space-y-2 text-sm">
                <p className="flex justify-between">
                  <span className="text-gray-600">Temperature:</span>
                  <span className="font-medium">{openweathermap_forecast.current.temperature_celsius.toFixed(1)}°C</span>
                </p>
                <p className="flex justify-between">
                  <span className="text-gray-600">Feels Like:</span>
                  <span className="font-medium">{openweathermap_forecast.current.feels_like_celsius.toFixed(1)}°C</span>
                </p>
                <p className="flex justify-between">
                  <span className="text-gray-600">Humidity:</span>
                  <span className="font-medium">{openweathermap_forecast.current.humidity_percent}%</span>
                </p>
                <p className="flex justify-between">
                  <span className="text-gray-600">Wind Speed:</span>
                  <span className="font-medium">{openweathermap_forecast.current.wind_speed_ms.toFixed(1)} m/s</span>
                </p>
                <p className="flex justify-between">
                  <span className="text-gray-600">Pressure:</span>
                  <span className="font-medium">{openweathermap_forecast.current.pressure_hpa.toFixed(0)} hPa</span>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Difference Metrics - Only show if we have current comparison data */}
        {!isIncomplete && metrics.current && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h5 className="font-semibold mb-2 text-gray-700">Differences</h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span className="text-gray-600">Temperature:</span>
                <span className="ml-2 font-medium">{Math.abs(metrics.current.temperature_difference_celsius).toFixed(1)}°C</span>
              </div>
              <div>
                <span className="text-gray-600">Humidity:</span>
                <span className="ml-2 font-medium">{Math.abs(metrics.current.humidity_difference_percent)}%</span>
              </div>
              <div>
                <span className="text-gray-600">Wind:</span>
                <span className="ml-2 font-medium">{Math.abs(metrics.current.wind_speed_difference_ms).toFixed(1)} m/s</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-600">Condition:</span>
                {metrics.current.condition_match ? (
                  <CheckCircle className="w-4 h-4 text-green-600 ml-2" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600 ml-2" />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Daily Forecast Comparison - Only show if we have daily comparison data */}
      {!isIncomplete && metrics.daily_comparison && metrics.daily_comparison.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
          <h3 className="text-xl font-semibold mb-4">7-Day Forecast Comparison</h3>

          <div className="space-y-3">
            {metrics.daily_comparison.slice(0, 7).map((day, index) => {
              const e2sDay = earth2studio_forecast?.daily[index];
              const owmDay = openweathermap_forecast?.daily[index];

            return (
              <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-700">
                    Day {day.day + 1} - {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  {day.condition_match ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">AI High/Low</p>
                    <p className="font-medium">
                      {e2sDay ? `${e2sDay.temp_high_celsius.toFixed(0)}° / ${e2sDay.temp_low_celsius.toFixed(0)}°` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Real High/Low</p>
                    <p className="font-medium">
                      {owmDay ? `${owmDay.temp_high_celsius.toFixed(0)}° / ${owmDay.temp_low_celsius.toFixed(0)}°` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">High Diff</p>
                    <p className="font-medium text-orange-600">
                      {Math.abs(day.temp_high_diff_celsius).toFixed(1)}°C
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Low Diff</p>
                    <p className="font-medium text-blue-600">
                      {Math.abs(day.temp_low_diff_celsius).toFixed(1)}°C
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Sources Available */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm text-gray-600">
        <p className="font-medium mb-2">Data Sources:</p>
        <div className="flex gap-4">
          <span className={comparison.sources_available.earth2studio ? 'text-green-600' : 'text-gray-400'}>
            {comparison.sources_available.earth2studio ? '✓' : '✗'} Earth-2 Studio
          </span>
          <span className={comparison.sources_available.openweathermap ? 'text-green-600' : 'text-gray-400'}>
            {comparison.sources_available.openweathermap ? '✓' : '✗'} OpenWeatherMap
          </span>
        </div>
        <p className="mt-2 text-xs">Last updated: {new Date(comparison.timestamp).toLocaleString()}</p>
      </div>
    </div>
  );
};

export default WeatherComparison;
